import React, { useMemo, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Stack,
    Button,
    AppBar,
    Toolbar,
    Divider,
    TextField,
    InputAdornment,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import logo_cinza from '../../assets/logo_branca.png';

// Verbetes transcritos do "Glossário dos Termos de Uso e Política da Empiria",
// versão de 22 de julho de 2025. A ordem é a do documento, não a alfabética:
// aqui o texto vale como parte dos Termos, então não cabe reordenar.
const VERBETES = [
    {
        termo: 'Acompanhamento',
        definicao:
            'Funcionalidade disponibilizada pela Plataforma por meio da qual o Conviva passa a acompanhar as atividades de um Anfitrião, recebendo notificações sobre novos Encontros criados por aquele Anfitrião, incluindo o respectivo valor e os horários disponíveis em sua Agenda, sem que isso implique o compartilhamento de dados de contato pessoal entre as partes, nos termos do item 1.12 dos Termos e do item 6.1.6.1 da Política de Cookies e Privacidade.',
    },
    {
        termo: 'Agenda',
        definicao:
            'Um espaço online, com data e horário, projetado para registrar quando os Encontros ocorrerão e para mostrar a disponibilidade do Anfitrião.',
    },
    {
        termo: 'Agendamento',
        definicao:
            'Condição escolhida pelo Anfitrião em que o Conviva poderá fazer o pagamento do valor proposto e garantir o Encontro.',
    },
    {
        termo: 'Anfitrião',
        definicao:
            'Usuário que cria e define as regras de um Encontro em seu nome e é titular exclusivo de todos os direitos de propriedade intelectual relativos ao conteúdo compartilhado, ou que está regularmente autorizado pelos titulares desses direitos a criar, divulgar e comercializar o Encontro na Plataforma.',
    },
    {
        termo: 'Conviva',
        definicao: 'Usuário que possui interesse em participar de um Encontro com um Anfitrião.',
    },
    {
        termo: 'Comprador',
        definicao:
            'Usuário (Conviva) que compra o direito de participar de um Encontro disponível na Plataforma.',
    },
    {
        termo: 'Dados Cadastrais',
        definicao:
            'São dados como a filiação, o endereço (inclusive o endereço eletrônico) e a qualificação pessoal, entendida como nome, prenome, estado civil e profissão do Usuário.',
    },
    {
        termo: 'Encontro',
        definicao:
            'Momento e local onde o Anfitrião e o Conviva se encontram em uma sala de videoconferência.',
    },
    {
        termo: 'Nota do Anfitrião',
        definicao:
            'Pontuação atribuída ao Anfitrião a partir das avaliações registradas pelos Convivas ao final de cada Encontro, que pode ser utilizada pela Empiria como um dos critérios de posicionamento e destaque do Anfitrião no marketplace da Plataforma, nos termos do item 5.5.1 dos Termos.',
    },
    {
        termo: 'Plataforma Empiria ou Plataforma',
        definicao:
            'Tanto o site (www.empiriapp.com.br) e seus subdomínios, como quaisquer outros sites, interfaces ou aplicativos nos quais a Empiria disponibilize seus recursos.',
    },
    {
        termo: 'Políticas da Empiria',
        definicao:
            'Coletivamente, a Política Geral de Uso Responsável, Política Geral de Compra e Pagamento, Política de Segurança Cibernética e da Informação e a Política de Cookies e Privacidade.',
    },
    {
        termo: 'Conteúdo',
        definicao:
            'Qualquer informação em formato de vídeo, áudio ou texto produzida pelo Anfitrião e/ou pelo Comprador e disponibilizada na Plataforma, incluindo, mas não se limitando à produção dos Encontros.',
    },
    {
        termo: 'Serviços da Empiria ou Serviços',
        definicao:
            'Coletivamente, os recursos disponibilizados pela Empiria possibilitam que o Usuário consuma, crie, divulgue ou comercialize Encontros na Plataforma.',
    },
    {
        termo: 'Serviços de Terceiros',
        definicao:
            'Sites, aplicativos ou recursos de terceiros disponibilizados para os Usuários por meio da Plataforma.',
    },
    {
        termo: 'Sorteio',
        definicao:
            'Tipo de condição escolhida pelo Anfitrião em que apenas um dos Convivas inscritos no encontro será selecionado para participar dele, enquanto os demais terão o direito de acessar o encontro apenas como espectadores.',
    },
    {
        termo: 'Taxa de Intermediação',
        definicao:
            'Taxa que a Empiria cobra do Anfitrião pelo uso dos recursos de pagamento da Empiria, cujo valor varia conforme o preço de venda do Produto.',
    },
    {
        termo: 'Terceiros Relacionados',
        definicao:
            'Quaisquer pessoas, naturais ou jurídicas, que estejam diretas ou indiretamente relacionadas a um Encontro, como especialistas, atores, personagens ou garotos-propaganda.',
    },
    {
        termo: 'Termos',
        definicao: 'Os Termos de Uso da Empiria.',
    },
    {
        termo: 'Usuário',
        definicao:
            'Qualquer pessoa que acesse ou use a Plataforma, independentemente de ter feito ou não o seu cadastro como Anfitrião ou Conviva.',
    },
];

// Busca sem acento e sem caixa: quem procura "servicos" precisa achar "Serviços".
const normalizar = texto =>
    texto
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

const Glossary = () => {
    const navigate = useNavigate();
    const [busca, setBusca] = useState('');
    // Aba aberta pelo aceite de politicas (target="_blank") nao tem para onde
    // voltar: e a primeira entrada do proprio historico dela.
    const [temParaOndeVoltar] = useState(() => window.history.length > 1);

    const encontrados = useMemo(() => {
        const termo = normalizar(busca.trim());
        if (!termo) return VERBETES;
        return VERBETES.filter(
            item => normalizar(item.termo).includes(termo) || normalizar(item.definicao).includes(termo),
        );
    }, [busca]);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
            {/* Header */}
            <AppBar position="static" elevation={2} sx={{ backgroundColor: '#006a61' }}>
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 } }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={1}
                            sx={{ flexGrow: 1, cursor: 'pointer' }}
                            onClick={() => navigate('/')}
                        >
                            <Box
                                component="img"
                                src={logo_cinza}
                                alt="Empiria"
                                sx={{ width: 35, height: 35, objectFit: 'contain' }}
                            />
                            <Typography variant="h6" sx={{ color: 'white' }}>
                                Empiria
                            </Typography>
                        </Stack>
                        {temParaOndeVoltar && (
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate(-1)}
                                sx={{ color: 'white', textTransform: 'none' }}
                            >
                                Voltar
                            </Button>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Conteudo */}
            <Container maxWidth="md" sx={{ py: 3 }}>
                <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="#006a61" gutterBottom textAlign="center">
                        Glossário
                    </Typography>

                    <Typography variant="body2" color="text.secondary" mb={4} textAlign="center">
                        Versão atualizada em 22 de julho de 2025
                    </Typography>

                    <Divider sx={{ mb: 4 }} />

                    <Typography variant="body1" color="text.secondary" paragraph sx={{ textAlign: 'justify', lineHeight: 1.8 }}>
                        Este Glossário integra os Termos de Uso da Empiria. Ele relaciona o significado das expressões
                        iniciadas por letras maiúsculas nos Termos ou nas demais Políticas da Empiria.
                    </Typography>

                    <Typography variant="body1" color="text.secondary" paragraph sx={{ textAlign: 'justify', lineHeight: 1.8 }}>
                        Sugerimos que leia as definições a seguir de forma cuidadosa e atenta.
                    </Typography>

                    <TextField
                        fullWidth
                        size="small"
                        value={busca}
                        onChange={event => setBusca(event.target.value)}
                        placeholder="Buscar um termo"
                        sx={{ my: 3 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#006a61' }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {encontrados.length === 0 ? (
                        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                            Nenhum termo encontrado para "{busca}".
                        </Typography>
                    ) : (
                        encontrados.map(item => (
                            <Box key={item.termo} mb={3}>
                                <Typography variant="h6" fontWeight="bold" color="#006a61" gutterBottom>
                                    {item.termo}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ textAlign: 'justify', lineHeight: 1.8 }}
                                >
                                    {item.definicao}
                                </Typography>
                            </Box>
                        ))
                    )}

                    {/* Botao de voltar */}
                    {temParaOndeVoltar && (
                        <Stack direction="row" justifyContent="center" mt={4}>
                            <Button
                                variant="contained"
                                onClick={() => navigate(-1)}
                                sx={{
                                    bgcolor: '#006a61',
                                    '&:hover': { bgcolor: '#1E7B54' },
                                    px: 4,
                                    py: 1.5,
                                    textTransform: 'none',
                                }}
                            >
                                Voltar
                            </Button>
                        </Stack>
                    )}
                </Paper>
            </Container>

            {/* Footer */}
            <Box sx={{ py: 3, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="body2" color="text.secondary">
                    © Todos os direitos reservados a Empiria
                </Typography>
            </Box>
        </Box>
    );
};

export default Glossary;
